import Button from "@amzn/meridian/button";
import Icon from "@amzn/meridian/icon";
import Link from "@amzn/meridian/link";
import pencilTokens from "@amzn/meridian-tokens/base/icon/pencil";
import plusTokens from "@amzn/meridian-tokens/base/icon/plus";
import trashTokens from "@amzn/meridian-tokens/base/icon/trash";
import {
  canManageRegion,
  formatDate,
  regionReportUrl,
  regionShortLabel,
} from "../adminReportUtils";
import { PolicyDocument } from "../types";

interface DocumentTestCaseProps {
  document: PolicyDocument;
  onAdd: () => void;
  onEdit: () => void;
  onRemove: () => void;
}

/** A document carries at most one test case, so this is a panel, not a list. */
const DocumentTestCase = ({
  document,
  onAdd,
  onEdit,
  onRemove,
}: DocumentTestCaseProps) => {
  const { testCase } = document;
  // Only this realm's own site can test a question, so another realm's test
  // cases are shown here and edited there.
  const manageable = canManageRegion(document.region);

  const manageElsewhere = (
    <Link
      href={regionReportUrl(document.region, "documents")}
      target="_blank"
      rel="noopener noreferrer"
    >
      {`Manage in ${regionShortLabel(document.region)}`}
    </Link>
  );

  if (!testCase) {
    return (
      <div className="admin-report__card-body">
        <div className="admin-report__table-empty">
          No test case yet. Nothing checks that this document can be retrieved.
        </div>
        <div className="admin-report__row-actions">
          {manageable ? (
            <Button size="small" onClick={onAdd}>
              <Icon tokens={plusTokens} />
              Add Test Case
            </Button>
          ) : (
            manageElsewhere
          )}
        </div>
      </div>
    );
  }

  return (
    <table className="admin-report__table">
      <thead>
        <tr>
          <th>Test Case ID</th>
          <th>Question</th>
          <th>Added By</th>
          <th aria-label="Actions" />
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>
            <span className="admin-report__mono">{testCase.testCaseId}</span>
          </td>
          <td>{testCase.question}</td>
          <td>
            <div>{testCase.createdBy}</div>
            <div className="admin-report__doc-sub">
              {formatDate(testCase.createdAt)}
            </div>
          </td>
          <td>
            {manageable ? (
              <div className="admin-report__row-actions">
                <Button
                  type="icon"
                  size="small"
                  aria-label="Edit test case"
                  onClick={onEdit}
                >
                  <Icon tokens={pencilTokens} />
                </Button>
                <Button
                  type="icon"
                  size="small"
                  aria-label="Remove test case"
                  onClick={onRemove}
                >
                  <Icon tokens={trashTokens} />
                </Button>
              </div>
            ) : (
              manageElsewhere
            )}
          </td>
        </tr>
      </tbody>
    </table>
  );
};

export default DocumentTestCase;
